import React from "react";
import { DateTimePicker } from "src/components/ui/DateTimePicker";
import { Input, Textarea } from "src/components/ui/Input";
import { Switch } from "src/components/ui/Switch";
import { HappeningNowPlatformChips } from "src/components/HappeningNow/HappeningNowPlatformChips";
import { Constants } from "src/constant";
import type { ComposerDefaults } from "src/lib/happeningNowComposerDefaults";
import {
  isStagedCard,
  type ComposerCard,
  type ComposerCardPatch,
} from "src/store/happeningNowComposerStore";

const labelClass =
  "flex flex-col gap-2 text-sm font-extrabold uppercase tracking-wide text-white/70";

interface ComposerCardEditorProps {
  card: ComposerCard;
  index: number;
  defaults: ComposerDefaults;
  onPatch: (patch: ComposerCardPatch) => void;
}

const customTag = (
  <span className="rounded-full bg-[#EEF2FF] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[#4338CA] ring-1 ring-[#6366F1]/30">
    custom
  </span>
);

/**
 * Editor for the currently-selected staged card. Every field is per-card;
 * expiry and platforms show a "custom" tag once they diverge from the defaults.
 */
export const ComposerCardEditor: React.FC<ComposerCardEditorProps> = ({
  card,
  index,
  defaults,
  onPatch,
}) => {
  const staged = isStagedCard(card);
  // "custom" only means something for staged cards (which start from defaults);
  // committed cards are always independent, so don't tag them.
  const platformsCustom =
    staged &&
    (card.platformAndroid !== defaults.platformAndroid ||
      card.platformIos !== defaults.platformIos);
  const expiryCustom =
    staged && card.expiresAtLocal !== defaults.expiresAtLocal;

  // A scheduled start is bounded by the card's expiry (must be before it) and a
  // ~1-month-out cap — so the picker can't offer an invalid date.
  const maxStartMs =
    Date.now() +
    Constants.HAPPENING_NOW_MAX_START_AHEAD_DAYS * 24 * 60 * 60 * 1000;
  const expiryMs = card.expiresAtLocal
    ? new Date(card.expiresAtLocal).getTime()
    : NaN;
  const startMax = new Date(
    Number.isNaN(expiryMs) ? maxStartMs : Math.min(expiryMs, maxStartMs),
  );

  return (
    <section
      className="rounded-[1.75rem] border-4 border-white/10 bg-white/[0.04] p-5 shadow-[0_6px_0_rgba(0,0,0,0.3)] backdrop-blur-sm sm:p-6"
      aria-label={`Editing card ${index + 1}`}
    >
      <div className="mb-4 flex items-center gap-2">
        <h2 className="text-lg font-extrabold text-white">
          Editing card {index + 1}
        </h2>
        {staged ? (
          <span className="rounded-full bg-[#EEF2FF] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[#4338CA] ring-1 ring-[#6366F1]/40">
            new
          </span>
        ) : card.dirty ? (
          <span className="rounded-full bg-[#FEF3C7] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[#92400E] ring-1 ring-[#F59E0B]/40">
            edited
          </span>
        ) : (
          <span className="rounded-full bg-[#ECFDF5] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[#0F766E] ring-1 ring-[#14B8A6]/40">
            in draft
          </span>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <label className={labelClass}>
          Title
          <Input
            accent="teal"
            type="text"
            value={card.title}
            onChange={(e) => onPatch({ title: e.target.value })}
            maxLength={120}
            placeholder="Spring kickoff event"
          />
        </label>

        <label className={labelClass}>
          Target link
          <Input
            accent="teal"
            type="url"
            value={card.targetLink}
            onChange={(e) => onPatch({ targetLink: e.target.value })}
            placeholder="https://…"
          />
        </label>

        <label className={labelClass}>
          Description (optional)
          <Textarea
            accent="teal"
            value={card.description}
            onChange={(e) => onPatch({ description: e.target.value })}
            maxLength={500}
            rows={2}
            placeholder="One sentence — what's the hook?"
          />
        </label>

        <div className="flex items-center gap-2">
          <HappeningNowPlatformChips
            tone="dark"
            groupLabel="Platforms"
            androidSelected={card.platformAndroid}
            iosSelected={card.platformIos}
            onToggleAndroid={() => {
              const next = !card.platformAndroid;
              if (!next && !card.platformIos) return;
              onPatch({ platformAndroid: next });
            }}
            onToggleIos={() => {
              const next = !card.platformIos;
              if (!next && !card.platformAndroid) return;
              onPatch({ platformIos: next });
            }}
          />
          {platformsCustom && customTag}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className={labelClass}>
            <span className="flex items-center gap-2">Starts showing at</span>
            <DateTimePicker
              accent="teal"
              value={card.startsAtLocal}
              onChanged={({ value }) => onPatch({ startsAtLocal: value })}
              end={startMax}
              aria-label="Card scheduled start date and time"
            />
            <span className="text-[11px] font-bold normal-case tracking-normal text-white/50">
              Leave empty to show immediately.
            </span>
          </label>

          <label className={labelClass}>
            <span className="flex items-center gap-2">
              Stops showing at {expiryCustom && customTag}
            </span>
            <DateTimePicker
              accent="teal"
              value={card.expiresAtLocal}
              onChanged={({ value }) => onPatch({ expiresAtLocal: value })}
              required
              aria-label="Card expiry date and time"
            />
          </label>
        </div>

        <label className={`${labelClass} sm:max-w-xs`}>
          Status
          <span className="flex min-h-[48px] items-center justify-between gap-3 rounded-2xl border-2 border-white/15 bg-white/5 px-4">
            <span className="text-sm font-extrabold text-white">
              {card.active ? "Active — visible" : "Paused — hidden"}
            </span>
            <Switch
              checked={card.active}
              onChange={(next) => onPatch({ active: next })}
              aria-label="Toggle active state"
            />
          </span>
        </label>
      </div>
    </section>
  );
};
